import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox } from "@/components/common/States";
import { AuthShell, Field, Submit, GoogleButton } from "./shared";
import SEO from "@/components/common/SEO";

export function useAfterLogin() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  return (data) => {
    signIn(data.token, data.user);
    toast.success("Giriş yapıldı");
    nav(data.user.profile_complete === false ? "/profil-tamamla" : params.get("next") || "/");
  };
}

export default function Login() {
  const after = useAfterLogin();
  const [form, setForm] = useState({ email: "", password: "" });
  const [twoFA, setTwoFA] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (twoFA) {
        after((await api.post("/auth/login/2fa", { email: twoFA, code })).data);
      } else {
        const { data } = await api.post("/auth/login", form);
        if (data.requires_2fa) { setTwoFA(data.email); toast.info("Giriş kodu e-postanıza gönderildi"); }
        else after(data);
      }
    } catch (e2) {
      setErr(errMsg(e2));
      setLocked(e2?.response?.status === 423);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={twoFA ? "2 aşamalı doğrulama" : "Giriş yap"} subtitle={twoFA ? `${twoFA} adresine gelen 6 haneli kodu girin` : "CreateFPV hesabınıza giriş yapın"} testId="login-page">
      <SEO title="Giriş Yap" description="CreateFPV hesabınıza giriş yapın ve ilanlarınızı yönetin." noIndex />
      <form onSubmit={submit} className="space-y-4">
        {twoFA ? (
          <Field id="login-2fa-code-input" label="Doğrulama kodu" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
        ) : (
          <>
            <Field id="login-email-input" label="E-posta" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Field id="login-password-input" label="Şifre" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          </>
        )}
        {err && <ErrorBox message={err} />}
        {locked && <Link to={`/hesap-kilidi?email=${encodeURIComponent(form.email)}`} data-testid="login-unlock-link" className="block text-sm text-[#0077ed] underline">Hesap kilidini e-posta koduyla aç</Link>}
        <Submit busy={busy} testId="login-submit-button">{twoFA ? "Doğrula ve giriş yap" : "Giriş yap"}</Submit>
      </form>
      {!twoFA && (
        <>
          <GoogleButton />
          <div className="flex flex-wrap justify-between gap-2 text-sm">
            <Link to="/sifremi-unuttum" data-testid="login-forgot-password-link" className="text-zinc-500 hover:text-zinc-900">Şifremi unuttum</Link>
            <Link to="/hesap-kilidi" data-testid="login-unlock-page-link" className="text-zinc-500 hover:text-zinc-900">Hesap kilidini aç</Link>
            <Link to="/kayit" data-testid="login-register-link" className="text-[#0077ed]">Hesap oluştur</Link>
          </div>
        </>
      )}
    </AuthShell>
  );
}
